import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
    fetchGetAllTemplates,
    fetchGetActiveTemplates,
    fetchGetTemplateById,
    fetchCreateTemplate,
    fetchUpdateTemplate,
    fetchDeleteTemplate,
    fetchGetDashboard, fetchGetMaintenanceTasks, fetchGetMaintenanceTasksByMachinery,
} from "./actions";
import {IMaintenanceDashboardRow, IMaintenanceTemplate} from "../../../models/IMaintenance";
import {ITask} from "../../../models/IMachineryTasks";

interface IMaintenanceState {
    // Шаблоны регламентов
    templates: IMaintenanceTemplate[];
    templatesLoading: boolean;
    currentTemplate: IMaintenanceTemplate | null;

    // Дашборд ТО
    dashboard: IMaintenanceDashboardRow[];
    dashboardLoading: boolean;

    // Задачи ТО
    history: ITask[];
    historyLoading: boolean;

    /** Фильтр истории ТО по типу техники. null = все типы. */
    historyTypeFilter: number | null;
}

const initialState: IMaintenanceState = {
    templates: [],
    templatesLoading: false,
    currentTemplate: null,
    dashboard: [],
    dashboardLoading: false,
    history: [],
    historyLoading: false,
    historyTypeFilter: null,
};

export const MaintenanceSlice = createSlice({
    name: "maintenance",
    initialState,
    reducers: {
        setTemplates: (
            state,
            action: PayloadAction<IMaintenanceTemplate[]>,
        ) => {
            state.templates = action.payload;
        },
        setCurrentTemplate: (
            state,
            action: PayloadAction<IMaintenanceTemplate | null>,
        ) => {
            state.currentTemplate = action.payload;
        },
        setMaintenanceHistoryTypeFilter: (
            state,
            action: PayloadAction<number | null>,
        ) => {
            state.historyTypeFilter = action.payload;
        },
        /**
         * Сброс при уходе со страницы деталей машины: история ТО привязана к
         * машине, и без сброса следующая показывала бы чужие работы, пока не
         * придёт ответ.
         */
        clearMaintenanceHistory: (state) => {
            state.history = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // ── Get All Templates ───────────────────────────────────────
            .addCase(
                fetchGetAllTemplates.fulfilled,
                (state, action: PayloadAction<IMaintenanceTemplate[]>) => {
                    state.templates = action.payload.sort(
                        (a, b) =>
                            new Date(b.created_at).getTime() -
                            new Date(a.created_at).getTime(),
                    );
                    state.templatesLoading = false;
                },
            )
            .addCase(fetchGetAllTemplates.pending, (state) => {
                state.templatesLoading = true;
            })
            .addCase(fetchGetAllTemplates.rejected, (state) => {
                state.templatesLoading = false;
            })

            // ── Get Active Templates ────────────────────────────────────
            .addCase(
                fetchGetActiveTemplates.fulfilled,
                (state, action: PayloadAction<IMaintenanceTemplate[]>) => {
                    state.templates = action.payload;
                    state.templatesLoading = false;
                },
            )
            .addCase(fetchGetActiveTemplates.pending, (state) => {
                state.templatesLoading = true;
            })
            .addCase(fetchGetActiveTemplates.rejected, (state) => {
                state.templatesLoading = false;
            })

            // ── Get Template By Id ──────────────────────────────────────
            .addCase(
                fetchGetTemplateById.fulfilled,
                (state, action: PayloadAction<IMaintenanceTemplate>) => {
                    state.currentTemplate = action.payload;
                    state.templatesLoading = false;
                },
            )
            .addCase(fetchGetTemplateById.pending, (state) => {
                state.templatesLoading = true;
            })
            .addCase(fetchGetTemplateById.rejected, (state) => {
                state.templatesLoading = false;
            })

            // ── Create Template ─────────────────────────────────────────
            .addCase(
                fetchCreateTemplate.fulfilled,
                (state, action: PayloadAction<IMaintenanceTemplate>) => {
                    state.templates = [...state.templates, action.payload];
                    state.templatesLoading = false;
                },
            )
            .addCase(fetchCreateTemplate.pending, (state) => {
                state.templatesLoading = true;
            })
            .addCase(fetchCreateTemplate.rejected, (state) => {
                state.templatesLoading = false;
            })

            // ── Update Template ─────────────────────────────────────────
            .addCase(
                fetchUpdateTemplate.fulfilled,
                (state, action: PayloadAction<IMaintenanceTemplate>) => {
                    state.templates = state.templates.map((t) =>
                        t.id === action.payload.id ? action.payload : t,
                    );
                    state.currentTemplate = action.payload;
                    state.templatesLoading = false;
                },
            )
            .addCase(fetchUpdateTemplate.pending, (state) => {
                state.templatesLoading = true;
            })
            .addCase(fetchUpdateTemplate.rejected, (state) => {
                state.templatesLoading = false;
            })

            // ── Delete Template ─────────────────────────────────────────
            .addCase(
                fetchDeleteTemplate.fulfilled,
                (state, action: PayloadAction<string>) => {
                    state.templates = state.templates.filter(
                        (t) => t.id !== action.payload,
                    );
                    if (state.currentTemplate?.id === action.payload) {
                        state.currentTemplate = null;
                    }
                    state.templatesLoading = false;
                },
            )
            .addCase(fetchDeleteTemplate.pending, (state) => {
                state.templatesLoading = true;
            })
            .addCase(fetchDeleteTemplate.rejected, (state) => {
                state.templatesLoading = false;
            })

            // ── Dashboard ───────────────────────────────────────────────
            .addCase(
                fetchGetDashboard.fulfilled,
                (state, action: PayloadAction<IMaintenanceDashboardRow[]>) => {
                    state.dashboard = action.payload;
                    state.dashboardLoading = false;
                },
            )
            .addCase(fetchGetDashboard.pending, (state) => {
                state.dashboardLoading = true;
            })
            .addCase(fetchGetDashboard.rejected, (state) => {
                state.dashboardLoading = false;
            })
            // ── Maintenance Tasks (company) ─────────────────────────────
            .addCase(
                fetchGetMaintenanceTasks.fulfilled,
                (state, action: PayloadAction<ITask[]>) => {
                    state.history = action.payload;
                    state.historyLoading = false;
                },
            )
            .addCase(fetchGetMaintenanceTasks.pending, (state) => {
                state.historyLoading = true;
            })
            .addCase(fetchGetMaintenanceTasks.rejected, (state) => {
                state.historyLoading = false;
            })

            // ── Maintenance Tasks (by machinery) ────────────────────────
            .addCase(
                fetchGetMaintenanceTasksByMachinery.fulfilled,
                (state, action: PayloadAction<ITask[]>) => {
                    state.history = action.payload;
                    state.historyLoading = false;
                },
            )
            .addCase(fetchGetMaintenanceTasksByMachinery.pending, (state) => {
                state.historyLoading = true;
            })
            .addCase(fetchGetMaintenanceTasksByMachinery.rejected, (state) => {
                state.historyLoading = false;
            });
    },
});

export const {
    setTemplates,
    setCurrentTemplate,
    setMaintenanceHistoryTypeFilter,
    clearMaintenanceHistory,
} = MaintenanceSlice.actions;

export default MaintenanceSlice.reducer;